import type { CSSProperties } from 'react';

import theme from '@/styles/theme';

type ResponsiveSvgImageProps = {
  alt: string;
  desktopHeight: number;
  desktopSrc: string;
  desktopWidth?: number;
  mobileHeight: number;
  mobileSrc: string;
  mobileWidth?: number;
};

/**
 * aspect-ratio 는 prop 으로 들어오는 런타임 값이라 클래스명으로 만들 수 없다.
 * CSS 변수로 넘기고, img 자손 선택자 + 미디어쿼리 분기는
 * globals.css 의 .responsive-svg-picture 에서 처리한다.
 */
type PictureCustomProperties = CSSProperties & {
  '--responsive-svg-ratio-desktop': string;
  '--responsive-svg-ratio-mobile': string;
  '--responsive-svg-max-width-mobile': string;
};

/**
 * How far past its drawn width the stacked artwork may be enlarged. It is a
 * tall column, so at a tablet's full width it would run to a couple of
 * thousand pixels; a little enlargement keeps it comfortable to read without
 * turning the section into a scroll.
 */
const MOBILE_MAX_SCALE = 1.45;

export default function ResponsiveSvgImage({
  alt,
  desktopHeight,
  desktopSrc,
  desktopWidth = 1104,
  mobileHeight,
  mobileSrc,
  mobileWidth = 1005,
}: ResponsiveSvgImageProps) {
  const style: PictureCustomProperties = {
    '--responsive-svg-ratio-desktop': `${desktopWidth} / ${desktopHeight}`,
    '--responsive-svg-ratio-mobile': `${mobileWidth} / ${mobileHeight}`,
    '--responsive-svg-max-width-mobile': `${Math.round(mobileWidth * MOBILE_MAX_SCALE)}px`,
  };

  return (
    <picture className="responsive-svg-picture block" style={style}>
      <source media={`(min-width: ${theme`screens.lg`})`} srcSet={desktopSrc} />
      <img
        alt={alt}
        decoding="async"
        height={mobileHeight}
        loading="lazy"
        src={mobileSrc}
        width={mobileWidth}
      />
    </picture>
  );
}
